import { fromJS, List } from 'immutable';
import { VisualizationTypes } from 'reporting-data/tsTypes/visualization';
import { VennVisualizationRecord } from 'reporting-visualizations/visualizations/venn/VennRecords';
import { isChartTypeColorPalette, isChartTypeColorTheme } from 'reporting-data/tsTypes/reportChart/color';
import { PaletteIds } from 'reporting-data/tsTypes/visualization/colors';
import { getPaletteColorScheme } from './colorUtils';
import { getColorPalette } from 'reporting-snowflake/relational/visualize/color/utils';
import { getDiscreteColors } from 'reporting-snowflake/relational/visualize/color/assign';
import { ColorOption } from 'reporting-snowflake/relational/schema/highcharts-settings';
import { getDeprecatedDefaultPalettes } from 'reporting-snowflake/relational/visualize/color/palettes';
import { pickTokenizedColors, pickPatterns
//@ts-expect-error Untyped import
} from 'reporting-ui-components/reports/utils/colors/colorPicker';
import { getPrimaryDataDataset, getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { getData } from 'reporting-data/v2/dataset/datasetRecords';
const DEFAULT_VENN_COLOR_OPTIONS = {
  paletteId: PaletteIds.Categorical14
};
const applyThemeToVennLayer = (colorConfig, keys, reportingThemes, brandKitPalettes) => {
  const theme = getPaletteColorScheme({
    colorConfig: fromJS(colorConfig),
    brandKitPalettes,
    reportingThemes
  });
  const pickedColors = pickTokenizedColors({
    colorConfig: fromJS(colorConfig),
    keys,
    customTheme: theme,
    reportingThemes
  });
  const definedPatterns = theme !== null && theme !== void 0 && theme.get('patterns') ? pickPatterns(theme.get('patterns'), keys) : undefined;
  return {
    defined: pickedColors,
    definedPatterns
  };
};
const applyPaletteToVennLayer = ({
  colorConfig,
  keys,
  brandKitPalettes,
  palettes
}) => {
  const palette = getColorPalette(ColorOption(colorConfig), brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('venn-apply-palette-fallback'), palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);

  // Get discrete colors from palette
  const colors = getDiscreteColors(fromJS(palette).get('scheme'), keys.length);

  // Create key -> color mapping
  const defined = fromJS(keys.reduce((acc, key, index) => {
    acc[key] = colors.get(index);
    return acc;
  }, {}));
  return {
    defined,
    definedPatterns: undefined
  };
};
const applyVennColorsToVennStyle = ({
  colorConfig,
  keys,
  brandKitPalettes,
  palettes,
  reportingThemes
}) => {
  if (isChartTypeColorTheme(colorConfig)) {
    return applyThemeToVennLayer(colorConfig, keys, reportingThemes, brandKitPalettes);
  } else if (isChartTypeColorPalette(colorConfig)) {
    return applyPaletteToVennLayer({
      colorConfig,
      keys,
      brandKitPalettes,
      palettes
    });
  } else {
    // Default to palette
    return applyPaletteToVennLayer({
      colorConfig: DEFAULT_VENN_COLOR_OPTIONS,
      keys,
      brandKitPalettes,
      palettes
    });
  }
};
const buildVennVisualization = ({
  datasetMap,
  vennReportChart,
  reportDetail,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  originalReport,
  brandKitPalettes,
  palettes,
  reportingThemes
}) => {
  const {
    layer,
    style
  } = vennReportChart;
  const {
    metrics,
    dimensions
  } = reportDetail;
  const {
    dataColumn = (metrics === null || metrics === void 0 ? void 0 : metrics[0]) || '',
    setsColumn = (dimensions === null || dimensions === void 0 ? void 0 : dimensions[0]) || ''
  } = layer || {};
  const primaryDataDataset = getPrimaryDataDataset(datasetMap);
  const visualizationPrimaryData = List.isList(primaryDataDataset) ? getPrimaryDataset(datasetMap) : primaryDataDataset;
  if (!visualizationPrimaryData) {
    throw new Error('No primary dataset available for Venn diagram');
  }

  // Extract segment IDs from the actual data
  const dataRows = getData(visualizationPrimaryData);
  // Extract individual segment IDs (rows where segment_ids is a string)
  const listIdsFromDataset = dataRows.filter(row => typeof row.get('segment_ids') === 'string').map(row => parseInt(row.get('segment_ids'), 10)).toList();
  const keys = listIdsFromDataset.map(String).toArray();
  const colorMapping = applyVennColorsToVennStyle({
    colorConfig: vennReportChart.color || DEFAULT_VENN_COLOR_OPTIONS,
    keys,
    brandKitPalettes,
    palettes,
    reportingThemes
  });
  const enhancedLayer = {
    dataset: 'primary',
    dataColumn,
    setsColumn,
    listIds: listIdsFromDataset
  };
  const defaultStyle = {
    dataLabels: {
      enabled: true
    },
    colors: colorMapping
  };
  const enhancedStyle = fromJS(Object.assign({}, defaultStyle, style));
  const result = VennVisualizationRecord({
    type: VisualizationTypes.VENN,
    data: fromJS({
      primary: visualizationPrimaryData
    }),
    layer: enhancedLayer,
    style: enhancedStyle
  });
  return result;
};
export default buildVennVisualization;